import { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form, Input, Modal } from 'antd';
import { BasicFormWrapper, Main } from '../../styled.js';
import { Button } from '../../../components/buttons/buttons.js';
import HttpStatus from '../../../utility/status.enum.js';
import Notification from '../../../utility/notification.js';
import Spinner from '../../common/Spinner.js';
import Heading from '../../../components/heading/heading';
import UilCamera from '@iconscout/react-unicons/icons/uil-camera';
import { getProfileV1, updateHostProfileV1 } from '../../../redux/userProfile/actionCreator';
import { uploadFile } from '../../../redux/uploadFile/actionCreator';
import CancelImg from '../../../static/img/Search-cancel.svg';
import { createHost } from '../../../redux/manageHost/actionCreator'; // for update profile using same
import Functions from '../../../utility/functions';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Cards } from '../../../components/cards/frame/cards-frame';

const UpdateManageProfile = forwardRef(({ onProfileUpdated }, ref) => {
  const { backEndPrefix, unsaveMessage } = global;
  const dispatch = useDispatch();
  const hostSlug = useParams()?.hostSlug ?? '';
  const isAdmin = !!hostSlug;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const buttonName = 'Save';

  const [loginUserSlug, setLoginUserSlug] = useState('');
  const [spinnerLoader, setSpinnerLoader] = useState(false);

  const [profilePreviewImage, setProfilePreviewImage] = useState('');
  const [profileLogo, setProfileLogo] = useState(null);
  const [showProfilImageValidation, setProfileImageValidation] = useState(false);

  const [updateProfileForm] = Form.useForm();  
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const getProfileFunction = async () => {
      try {
        setProfileImageValidation(false);
        setSpinnerLoader(true);
        const parms = hostSlug ? '?host_slug=' + hostSlug : '';
        const profileResponse = await dispatch(
          getProfileV1(parms, (response) => {
            return response;
          }),
        );
        if (profileResponse?.status !== HttpStatus.HTTP_OK) {
          // Show Error Alert
          setSpinnerLoader(false);
        } else {
          setLoginUserSlug(profileResponse?.result?.slug);
          setProfilePreviewImage(profileResponse?.result?.image);
          const fieldsValue = {};
          if (profileResponse?.result?.name) {
            fieldsValue.name = profileResponse.result.name;
          }
          if (profileResponse?.result?.display_name) {
            fieldsValue.display_name = profileResponse.result.display_name;
          }
          if (profileResponse?.result?.phone_number) {
            setPhone(profileResponse.result.phone_number);
          }
          if (profileResponse?.result?.email) {
            fieldsValue.email = profileResponse.result.email;
          }
          if (profileResponse?.result?.company_name) {
            fieldsValue.company_name = profileResponse.result.company_name;
          }
          if (profileResponse?.result?.reply_name_for_email !== undefined) {
            fieldsValue.reply_name_for_email = profileResponse.result.reply_name_for_email;
          }
          if (profileResponse?.result?.reply_email !== undefined) {
            fieldsValue.reply_email = profileResponse.result.reply_email;
          }
          updateProfileForm.setFieldsValue(fieldsValue);
          setSpinnerLoader(false);
        }
      } catch (error) {}
    };

    getProfileFunction();
  }, [hostSlug]);

  const handleSubmit = useCallback((values) => {
    if (!profilePreviewImage) {
      return false;
    }
    setSpinnerLoader(true);
    let host_slug = hostSlug;
    let requestData = { ...values, host_slug, phone_number: phone };
    if (!isAdmin) { // if email can be pass only for Admin Request otherwise not
      delete requestData.email;
    }

    if (profileLogo) {
      const image = profileLogo;
      requestData = { ...requestData, image };
    }
    const updateProfile = async () => {
      try {
        const updateProfileResponse = await dispatch(
          updateHostProfileV1(requestData, (response) => {
            return response;
          }),
        );
        if (updateProfileResponse?.status !== HttpStatus.HTTP_OK) {
          Notification.addNotificationError(updateProfileResponse.message);          
        } else {
          Notification.addNotificationSuccess(updateProfileResponse.message);
          // ✅ Call parent function after success
          if (onProfileUpdated) {
            onProfileUpdated(true);
          }
          // window.location.reload();
        }
        setSpinnerLoader(false);        
      } catch (error) {}
    };
    updateProfile();
  });

  // Expose proper form submit trigger
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      checkValidation();
      updateProfileForm.submit(); // triggers AntD validation + onFinish
    },
  }));

  // Start Image Upload Functionality
  const handleProfileImageChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
        if (!allowedTypes.includes(selectedFile.type)) {
          Notification.addNotificationError(
            'Unsupported image type. Please upload an image in JPG, PNG, GIF, BMP, or WebP format.',
          );
          return;
        }
        setSpinnerLoader(true);
        const compressedFile = await Functions.compressImage(selectedFile);
        const formData = new FormData();
        formData.append('imageType', 1);
        formData.append('file', compressedFile);
        const uploadFileResponse = await dispatch(uploadFile(formData));
        if (uploadFileResponse?.status === HttpStatus.HTTP_OK) {
          const fileName = uploadFileResponse?.result?.fileName;
          setProfileLogo(fileName);
          const reader = new FileReader();
          reader.onload = () => {
            setProfilePreviewImage(reader.result);
          };
          reader.readAsDataURL(compressedFile);
          setProfileImageValidation(false);
        }
        setSpinnerLoader(false);
      } catch (error) {}
    }
  };
  const deleteProfilePreviewImage = (e) => {
    setProfilePreviewImage('');
    setProfileLogo(null);
    setProfileImageValidation(true);
  };
  const checkValidation = () => {
    if (!profilePreviewImage) {
      setProfileImageValidation(true);
    }
    setTimeout(() => {
      const errorElements = document.querySelectorAll('.ant-form-item-has-error');
      if (errorElements.length > 0) {
        const firstErrorElement = errorElements[0] ?? 0;
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'start',
        });
      }
    }, 300);
  };

  const handlePhoneChange = (value) => {
    const phoneWithoutCode = value.replace(/^\+?\d{1,4}/, '').trim();
    if (phoneWithoutCode === '') {
      setPhone('');
    } else {
      setPhone(value);
    }
  };

  // Handle Cancel button event
  const handleCancel = (e) => {
    e.preventDefault();
    if (global.isDirtyGlobal) {
      const confirmCancel = window.confirm(unsaveMessage);
      if (confirmCancel) {
        global.isDirtyGlobal = false;
        // hostSlug found that means admin login
        onProfileUpdated();
      }
    } else {
      onProfileUpdated();
    }
  };

  const handleInputChange = () => {
    global.isDirtyGlobal = true;
  }

  return (
    <>
      <Spinner loader={spinnerLoader} />
      <Form form={updateProfileForm} name="update-profile" onFinish={handleSubmit} onValuesChange={handleInputChange}>
        <>
          <Cards xs={24} headless className="borderRadius common_fiter_table remove-min-h">
            <Main className="manage-shared-content-spacing">
              <BasicFormWrapper>
                <Row className="">
                  <Col xs={24} md={6}>
                    <div className="project-modal">
                      <div className="manage-list-profile">
                        <div className="upload-section details_upload_section">
                          <figure className="photo-upload align-start-v">
                            <figcaption>
                              <div className="info">
                                <Heading as="h4" className="required-label text-center">
                                  <span className="status error require image_label"></span> The avatar my guest see{' '}
                                </Heading>
                              </div>
                              <img
                                className={profilePreviewImage ? 'user_profile_image' : 'user_profile_image'}
                                src={profilePreviewImage || require('../../../static/img/avatar/profileImage.png')}
                                alt="Preview"
                              />
                              <label htmlFor="fileProfileInput" className="btn-upload">
                                <div
                                  className={`ant-upload-select ${
                                    showProfilImageValidation ? 'ant-upload-img-error' : ''
                                  }`}
                                >
                                  <span className="btn-upload">
                                    <UilCamera />
                                  </span>
                                </div>
                                <input
                                  type="file"
                                  id="fileProfileInput"
                                  accept="image/*"
                                  className="upload_img_input"
                                  onChange={handleProfileImageChange}
                                  onClick={(e) => {
                                    e.currentTarget.value = null;
                                  }}
                                />
                              </label>
                            </figcaption>
                          </figure>
                          {profileLogo && (
                            <span
                              onClick={() => deleteProfilePreviewImage('')}
                              className="status error status_delete_img"
                            >
                              <img src={CancelImg} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {showProfilImageValidation && (
                      <div className="showImageError host">
                        <div class="ant-form-item-has-error ant-form-item-explain-error">Please select image</div>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row gutter={15} className="mt-2">
                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item
                        label="What my guest see under my avatar"
                        name="display_name"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter display name',
                            transform: (value) => value?.trim(),
                          },
                        ]}
                        className="compact-label"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Input placeholder="What my guest see under my avatar" tabIndex="1" />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item
                        label="My management company's legal name for my rental agreements"
                        name="company_name"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter company name',
                            transform: (value) => value?.trim(),
                          },
                        ]}
                        className="compact-label"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Input placeholder="My management company's legal name for my rental agreements" tabIndex="2" />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item
                        className="compact-label"
                        label="The name auto-signing for my management company in my rental agreement"
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter name',
                            transform: (value) => value?.trim(),
                          },
                        ]}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Input placeholder="The name auto-signing for my management company in my rental agreement" tabIndex="3" />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="project-modal country-code-custom_box">
                      <Form.Item label="My primary pnone number on file in case Happy Guest needs to contact me directly" 
                        className="compact-label"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <PhoneInput
                          country={'us'} // default country
                          value={phone}
                          onChange={handlePhoneChange}
                          tabIndex="4"
                          inputProps={{
                            name: 'phone',
                            className: 'ant-input',
                          }}
                          placeholder="My primary pnone number on file in case Happy Guest needs to contact me directly"
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item 
                        label="The email I use to log in and receive notifications"
                        name="email"
                        rules={[
                          {
                            required: isAdmin ? true : false,
                            message: 'Please enter email',
                            transform: (value) => value?.trim(),
                          },
                        ]}
                        >
                        <Input
                          placeholder="The email I use to log in and receive notifications"
                          tabIndex="5"
                          className= "readonlyInput"                         
                          readOnly={!isAdmin}
                          disabled={!isAdmin}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
                <Row gutter={15} className="mt-2">
                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item
                        label="The name used when I send an email to the guest"
                        name="reply_name_for_email"
                        rules={[
                          {
                            max: 100,
                            message: 'Name cannot exceed 100 characters',
                          },
                          {
                            transform: (value) => value?.trim(),
                            validator: (_, value) => Promise.resolve(),
                          },
                        ]}
                        className="compact-label"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Input placeholder="The name used when I send an email to the guest" tabIndex="6" maxLength={100} />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="project-modal">
                      <Form.Item
                        label="The email used when the guest sends a reply"
                        name="reply_email"
                        rules={[
                          {
                            type: 'email',
                            message: 'Please enter a valid email address',
                            transform: (value) => value?.trim(),
                          },
                        ]}
                        className="compact-label"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Input placeholder="The email used when the guest sends a reply" tabIndex="7" maxLength={255} />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </BasicFormWrapper>
            </Main>
          </Cards>
          <div className="manage-listing-btn">
            {/* <Col xs={4}> */}
            <Button
              htmlType="submit"
              size="default"
              shape="circle"
              type="primary"
              key="submit"
              className="update-btn"
              onClick={checkValidation}
            >
              {spinnerLoader ? 'Loading...' : buttonName}
            </Button>
            <Button onClick={(e) => handleCancel(e)} shape="circle" className="form-cancel-btn" outlined type="primary">
              Cancel
            </Button> 
            {/* </Col> */}
          </div>
        </>
      </Form>
    </>
  );
});

export default UpdateManageProfile;
